import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { myDesignService } from "../../services";
import clsx from "clsx";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Container,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  IconButton,
} from "@mui/material";
import { Typography } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useSnackbar } from "notistack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import EventIcon from "@mui/icons-material/Event";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import GoldenSwitch from "../goldenSwitch/GoldenSwitch";
import TocIcon from "@mui/icons-material/Toc";

const PREFIX = "UserAuthorizationsManagement";

const classes = {
  paper: `${PREFIX}-paper`,
  targetContainer: `${PREFIX}-targetContainer`,
  marginBottom: `${PREFIX}-marginBottom`,
  parentHeading: `${PREFIX}-parentHeading`,
  heading: `${PREFIX}-heading`,
  secondaryHeading: `${PREFIX}-secondaryHeading`,
  parentAccordionContainer: `${PREFIX}-parentAccordionContainer`,
  titleArea: `${PREFIX}-titleArea`,
  actionButton: `${PREFIX}-actionButton`,
  iconButtonSize: `${PREFIX}-iconButtonSize`,
  clickableContent: `${PREFIX}-clickableContent`,
  buttonsContainer: `${PREFIX}-buttonsContainer`,
  dividerMargin: `${PREFIX}-dividerMargin`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.paper}`]: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.between("xs", "md")]: {
      maxWidth: "350px",
    },
    width: "100%",
    margin: "0 auto",
    padding: "2rem 0",
  },

  [`& .${classes.targetContainer}`]: {
    display: "flex",
    flexDirection: "column",
    "& > div": {
      backgroundColor: "#424242",
      border: "1px solid rgba(255,255,255,0.16)",
    },
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.parentHeading}`]: {
    fontSize: theme.typography.pxToRem(24),
    flexBasis: "50%",
    flexShrink: 0,
  },

  [`& .${classes.heading}`]: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: "50%",
    flexShrink: 0,
  },

  [`& .${classes.secondaryHeading}`]: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },

  [`& .${classes.parentAccordionContainer}`]: {
    marginBottom: "1rem !important",
  },

  [`& .${classes.titleArea}`]: {
    width: "90%",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },

  [`& .${classes.actionButton}`]: {
    margin: "0.5rem 0 0.5rem 0",
  },

  [`& .${classes.iconButtonSize}`]: {
    width: "65px",
    height: "65px",
  },

  [`& .${classes.clickableContent}`]: {
    display: "flex",
    flexDirection: "column",
    padding: "0 16px",
  },

  [`& .${classes.buttonsContainer}`]: {
    height: "40px",
    marginTop: "-1rem",
    marginBottom: "1rem",
    display: "flex",
    justifyContent: "space-evenly",
    width: "80%",
  },

  [`& .${classes.dividerMargin}`]: {
    marginBottom: "0.5rem",
  },
}));

const UserAuthorizationsManagement = (props) => {
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [authorizations, setAuthorizations] = useState(null);
  const [parentExpanded, setParentExpanded] = useState(null);
  const toastErrorAuth = () =>
    enqueueSnackbar(t("toasts.errorUpdateRights"), { variant: "error" });

  function getInfos() {
    myDesignService
      .getDetailsAuthorizations(props.frameId, props.selectedChip.id)
      .then((res) => {
        let tmp = {};
        res.forEach((item) => {
          if (tmp.hasOwnProperty(item.targetLabel))
            tmp[item.targetLabel].push(item);
          else tmp[item.targetLabel] = [item];
        });
        setAuthorizations(tmp);
      });
  }

  useEffect(() => {
    getInfos();
  }, []);

  const handleChange = (index) => {
    if (parentExpanded !== index) {
      setParentExpanded(index);
    } else {
      setParentExpanded(null);
    }
  };

  function areAllActivated() {
    let result = true;
    const keys = Object.keys(authorizations);
    keys.forEach((key) => {
      if (authorizations[key].findIndex((item) => item.authorized === 0) !== -1)
        result = false;
    });
    return result;
  }

  function getAllZonesByValue(value) {
    const keys = Object.keys(authorizations);
    let tmp = [];
    keys.forEach((key) => {
      authorizations[key].forEach((item) => {
        if (item.authorized === value) tmp.push(item.frameZoneId);
      });
    });
    return tmp;
  }

  if (authorizations) {
    const keys = Object.keys(authorizations);
    return (
      <Container>
        <Root>
          <div
            style={{ display: "flex", marginTop: "1rem", padding: "0 16px" }}
          >
            <Typography
              variant="h5"
              style={{ marginRight: "1rem", marginTop: "0.5rem" }}
            >
              {t("myDesign.privacy.updateAllRights")}
            </Typography>
            <FormControlLabel
              style={{ transform: "translateY(5px)" }}
              control={
                <GoldenSwitch
                  checked={areAllActivated()}
                  onChange={() => {
                    if (areAllActivated()) {
                      const zonesId = getAllZonesByValue(1);
                      myDesignService
                        .removeMultipleAccessZone(
                          props.frameId,
                          props.selectedChip.id,
                          zonesId,
                          toastErrorAuth
                        )
                        .then(() => getInfos());
                    } else {
                      const zonesId = getAllZonesByValue(0);
                      myDesignService
                        .addMultipleAccessZone(
                          props.frameId,
                          props.selectedChip.id,
                          zonesId,
                          toastErrorAuth
                        )
                        .then(() => getInfos());
                    }
                  }}
                  name="Authorized"
                  className={classes.secondaryHeading}
                />
              }
            />
          </div>
          <Divider
            className={classes.dividerMargin}
            style={{ marginTop: "1rem" }}
          />
          {keys.map((key, index) => (
            <div key={index + 1} className={classes.targetContainer}>
              <Accordion
                expanded={parentExpanded === index}
                onChange={() => handleChange(index)}
                className={classes.parentAccordionContainer}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}bh-parent`}
                  id={`panel${index}bh-header-parent`}
                >
                  <Typography
                    variant="h5"
                    className={clsx(classes.parentHeading)}
                  >
                    {key}
                  </Typography>
                  <FormControlLabel
                    control={
                      <GoldenSwitch
                        checked={
                          authorizations[key].findIndex(
                            (item) => item.authorized === 0
                          ) === -1
                        }
                        onChange={() => {
                          const zonesId = authorizations[key].map(
                            (item) => item.frameZoneId
                          );
                          if (
                            authorizations[key].findIndex(
                              (item) => item.authorized === 0
                            ) === -1
                          ) {
                            myDesignService
                              .removeMultipleAccessZone(
                                props.frameId,
                                props.selectedChip.id,
                                zonesId,
                                toastErrorAuth
                              )
                              .then(() => getInfos());
                          } else {
                            myDesignService
                              .addMultipleAccessZone(
                                props.frameId,
                                props.selectedChip.id,
                                zonesId,
                                toastErrorAuth
                              )
                              .then(() => getInfos());
                          }
                        }}
                        name="Authorized"
                        className={classes.secondaryHeading}
                      />
                    }
                  />
                </AccordionSummary>
                <Divider light className={classes.dividerMargin} />
                {authorizations[key].map((item, subIndex) => {
                  return (
                    <div
                      className={classes.clickableContent}
                      key={`child-${subIndex}`}
                    >
                      <div style={{ display: "flex", width: "100%" }}>
                        <Typography
                          variant="body1"
                          className={clsx(
                            classes.marginBottom,
                            classes.titleArea
                          )}
                        >
                          {item.zoneLabel}
                        </Typography>
                        <FormControlLabel
                          style={{ transform: "translateY(-5px)" }}
                          control={
                            <GoldenSwitch
                              checked={item.authorized === 1 ? true : false}
                              onChange={() => {
                                if (item.authorized === 1) {
                                  myDesignService
                                    .removeAccessToZone(
                                      props.frameId,
                                      props.selectedChip.id,
                                      item.id,
                                      toastErrorAuth
                                    )
                                    .then(() => getInfos());
                                } else {
                                  myDesignService
                                    .addAccessToZone(
                                      props.frameId,
                                      props.selectedChip.id,
                                      item.frameZoneId,
                                      toastErrorAuth
                                    )
                                    .then(() => getInfos());
                                }
                              }}
                              name="Authorized"
                            />
                          }
                        />
                      </div>
                      {item.authorized === 1 && (
                        <div className={classes.buttonsContainer}>
                          <IconButton
                            color="inherit"
                            aria-label="notify"
                            disableRipple
                            edge="end"
                            onClick={() =>
                              myDesignService
                                .updateOptionAccess(
                                  props.frameId,
                                  props.selectedChip.id,
                                  "notifyAccess",
                                  item.notifyAccess === 1 ? 0 : 1,
                                  item.id,
                                  toastErrorAuth
                                )
                                .then(() => getInfos())
                            }
                            className={classes.iconButtonSize}
                            size="large"
                          >
                            <SettingsInputAntennaIcon
                              className={classes.actionButton}
                              style={{
                                color:
                                  item.notifyAccess === 1
                                    ? theme.palette.primary.main
                                    : "#CFDBD5",
                              }}
                            />
                          </IconButton>
                          <IconButton
                            color="inherit"
                            aria-label="Preview"
                            disableRipple
                            edge="end"
                            onClick={() =>
                              myDesignService
                                .updateOptionAccess(
                                  props.frameId,
                                  props.selectedChip.id,
                                  "previewAccess",
                                  item.previewAccess === 1 ? 0 : 1,
                                  item.id,
                                  toastErrorAuth
                                )
                                .then(() => getInfos())
                            }
                            className={classes.iconButtonSize}
                            size="large"
                          >
                            <FindInPageIcon
                              className={classes.actionButton}
                              style={{
                                color:
                                  item.previewAccess === 1
                                    ? theme.palette.primary.main
                                    : "#CFDBD5",
                              }}
                            />
                          </IconButton>
                          <IconButton
                            color="inherit"
                            aria-label="save"
                            disableRipple
                            edge="end"
                            onClick={() =>
                              myDesignService
                                .updateOptionAccess(
                                  props.frameId,
                                  props.selectedChip.id,
                                  "saveAccess",
                                  item.saveAccess === 1 ? 0 : 1,
                                  item.id,
                                  toastErrorAuth
                                )
                                .then(() => getInfos())
                            }
                            className={classes.iconButtonSize}
                            size="large"
                          >
                            <SaveIcon
                              className={classes.actionButton}
                              style={{
                                color:
                                  item.saveAccess === 1
                                    ? theme.palette.primary.main
                                    : "#CFDBD5",
                              }}
                            />
                          </IconButton>
                          <IconButton
                            color="inherit"
                            aria-label="schedule"
                            disableRipple
                            edge="end"
                            onClick={() =>
                              myDesignService
                                .updateOptionAccess(
                                  props.frameId,
                                  props.selectedChip.id,
                                  "scheduleAccess",
                                  item.scheduleAccess === 1 ? 0 : 1,
                                  item.id,
                                  toastErrorAuth
                                )
                                .then(() => getInfos())
                            }
                            className={classes.iconButtonSize}
                            size="large"
                          >
                            <EventIcon
                              className={classes.actionButton}
                              style={{
                                color:
                                  item.scheduleAccess === 1
                                    ? theme.palette.primary.main
                                    : "#CFDBD5",
                              }}
                            />
                          </IconButton>
                          <IconButton
                            color="inherit"
                            aria-label="upload"
                            disableRipple
                            edge="end"
                            onClick={() =>
                              myDesignService
                                .updateOptionAccess(
                                  props.frameId,
                                  props.selectedChip.id,
                                  "uploadAccess",
                                  item.uploadAccess === 1 ? 0 : 1,
                                  item.id,
                                  toastErrorAuth
                                )
                                .then(() => getInfos())
                            }
                            className={classes.iconButtonSize}
                            size="large"
                          >
                            <CloudUploadIcon
                              className={classes.actionButton}
                              style={{
                                color:
                                  item.uploadAccess === 1
                                    ? theme.palette.primary.main
                                    : "#CFDBD5",
                              }}
                            />
                          </IconButton>
                          <IconButton
                            color="inherit"
                            aria-label="content"
                            disableRipple
                            edge="end"
                            onClick={() =>
                              myDesignService
                                .updateOptionAccess(
                                  props.frameId,
                                  props.selectedChip.id,
                                  "editContentAccess",
                                  item.editContentAccess === 1 ? 0 : 1,
                                  item.id,
                                  toastErrorAuth
                                )
                                .then(() => getInfos())
                            }
                            className={classes.iconButtonSize}
                            size="large"
                          >
                            <TocIcon
                              className={classes.actionButton}
                              style={{
                                color:
                                  item.editContentAccess === 1
                                    ? theme.palette.primary.main
                                    : "#CFDBD5",
                              }}
                            />
                          </IconButton>
                        </div>
                      )}
                      <Divider light className={classes.dividerMargin} />
                    </div>
                  );
                })}
              </Accordion>
            </div>
          ))}
        </Root>
      </Container>
    );
  } else {
    return (
      <Root>
        <div className={classes.paper}>
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </div>
      </Root>
    );
  }
};

export default UserAuthorizationsManagement;
